import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { CharacterMarkerGroup } from "../../board/ChapterBoard/types";
import { characterGroupsAtPlayhead } from "../../character/ActiveCharacterGroup/atPlayhead";
import { locationRunRole } from "../LocationRun/branchKey";
import type { LocationRun } from "../LocationRun/runs";
import type { ActiveLocationGroup, ActiveLocationMember } from "./index";
import { forkBranchRejoinShowsSite } from "../LocationRun/siteAnchor";
import {
  groupLabel,
  partPlaceId,
  primaryMember,
  runsOnBranch,
  secondaryMember,
  siteRunOnBranch,
} from "./branchRuns";

function activePrimaryOnBranch(
  layout: BookTimelineLayout,
  branchId: string,
  playheadMs: number,
): LocationRun | undefined {
  const onScene = runsOnBranch(layout, branchId).find((run) => {
    if (locationRunRole(run) !== "primary") {
      return false;
    }
    return playheadMs >= run.startMs && playheadMs < run.endMs;
  });
  if (onScene !== undefined) {
    return onScene;
  }
  const hasCohort = characterGroupsAtPlayhead(layout.rail, playheadMs).some(
    (cohort) => cohort.branchId === branchId,
  );
  const site = siteRunOnBranch(layout, branchId, playheadMs);
  if (site === undefined) {
    return undefined;
  }
  if (
    hasCohort
    || playheadMs < site.endMs
    || forkBranchRejoinShowsSite(layout.rail, branchId, playheadMs)
  ) {
    return site;
  }
  return undefined;
}

/** The visit under way at the playhead, else the next one, else the last. */
function partRunForPlace(
  layout: BookTimelineLayout,
  branchId: string,
  placeId: string,
  playheadMs: number,
): LocationRun | undefined {
  const visits = runsOnBranch(layout, branchId)
    .filter((run) => run.runId.includes(":") && partPlaceId(run) === placeId)
    .sort((a, b) => a.startMs - b.startMs);
  return visits.find((run) => playheadMs < run.endMs) ?? visits.at(-1);
}

export function buildUnifiedBranchGroup(
  layout: BookTimelineLayout,
  branchId: string,
  playheadMs: number,
  cohort?: CharacterMarkerGroup,
): ActiveLocationGroup | null {
  const runs = runsOnBranch(layout, branchId);
  const primary = activePrimaryOnBranch(layout, branchId, playheadMs);
  if (primary === undefined) {
    return null;
  }
  const members: ActiveLocationMember[] = [primaryMember(primary.runId)];
  for (const run of runs) {
    if (locationRunRole(run) !== "secondary") {
      continue;
    }
    members.push(secondaryMember(layout, run, playheadMs));
  }
  return {
    id: cohort?.id ?? `${branchId}:site`,
    branchId,
    placeId: cohort?.placeId,
    label: groupLabel(runs, members),
    members,
  };
}

export function buildCohortGroup(
  layout: BookTimelineLayout,
  cohort: CharacterMarkerGroup,
  playheadMs: number,
): ActiveLocationGroup | null {
  const runs = runsOnBranch(layout, cohort.branchId);
  const primary = activePrimaryOnBranch(layout, cohort.branchId, playheadMs);
  if (primary === undefined) {
    return null;
  }
  const members: ActiveLocationMember[] = [primaryMember(primary.runId)];
  const part = partRunForPlace(layout, cohort.branchId, cohort.placeId, playheadMs);
  if (part !== undefined) {
    members.push(secondaryMember(layout, part, playheadMs));
  }
  return {
    id: cohort.id,
    branchId: cohort.branchId,
    placeId: cohort.placeId,
    label: groupLabel(runs, members),
    members,
  };
}

export function buildRailPartGroup(
  layout: BookTimelineLayout,
  branchId: string,
  run: LocationRun,
  playheadMs: number,
): ActiveLocationGroup | null {
  const runs = runsOnBranch(layout, branchId);
  const primary = activePrimaryOnBranch(layout, branchId, playheadMs);
  if (primary === undefined) {
    return null;
  }
  const members = [
    primaryMember(primary.runId),
    secondaryMember(layout, run, playheadMs),
  ];
  return {
    id: run.runId,
    branchId,
    placeId: partPlaceId(run),
    label: groupLabel(runs, members),
    members,
  };
}
